<template>
  <WdsSelect
    :model-value="selectedOptions"
    multi-select
    can-deselect
    searchable
    :list="ownerOptions"
    :label="label"
    :placeholder="$t('Client owner(s)')"
    :max-labels-visible="maxLabelsVisible"
    :aria-label="$t('Client owner')"
    class="tw-w-full tw-min-w-0"
    data-test="client-owner-filter"
    @update:model-value="onChange"
  />
</template>

<script setup lang="ts">
import { WdsSelect } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed } from 'vue'
import { $t } from '@/composables/translation'
import { user } from '@/composables/useUser'
import { SupplierClientOwnersFilterOptionsDocument } from '@/gql/graphql'

type OwnerOption = { id: string; value: string; label: string }

const props = withDefaults(
  defineProps<{
    selectedOwners?: string[]
    paramName?: string
    label?: string
    maxLabelsVisible?: number
    navigate?: (url: string) => void
  }>(),
  {
    selectedOwners: () => [],
    paramName: 'clientOwners',
    label: undefined,
    maxLabelsVisible: 1,
    navigate: (url: string) => window.location.assign(url),
  },
)

// Matches both `clientOwners[]` and Laravel's indexed `clientOwners[0]`.
const isListKey = (key: string) =>
  key.startsWith(`${props.paramName}[`) && /^\[\d*\]$/.test(key.slice(props.paramName.length))

const selectedIds = computed<string[]>(() => {
  const params = new URLSearchParams(window.location.search)
  const ids = [...params.entries()]
    .filter(([key, value]) => isListKey(key) && value !== '')
    .map(([, value]) => value)

  if (ids.length || params.has(props.paramName)) {
    return ids
  }

  return props.selectedOwners
})

const ownerLabel = (owner: { id: string; label: string }) =>
  owner.id === user.value.globalId ? $t('Me (:user)', { user: owner.label }) : owner.label

const { result } = useQuery(
  SupplierClientOwnersFilterOptionsDocument,
  {},
  { clientId: 'publicApi' },
)

const ownerOptions = computed<OwnerOption[]>(() => {
  const owners = (result.value?.companyClients?.data ?? []).flatMap((client) =>
    (client?.owners ?? []).map((owner) => owner.user),
  )

  return Array.from(new Map(owners.map((owner) => [owner.id, owner])).values())
    .map((owner) => ({ ...owner, label: ownerLabel(owner) }))
    .sort((a, b) => (a.id === user.value.globalId ? -1 : b.id === user.value.globalId ? 1 : 0))
})

const selectedOptions = computed<OwnerOption[]>(() =>
  selectedIds.value.map(
    (id) =>
      ownerOptions.value.find((owner) => owner.value === id) ?? {
        id,
        value: id,
        label: ownerLabel({ id, label: id === user.value.globalId ? user.value.name : id }),
      },
  ),
)

const onChange = (owners: OwnerOption[] | null) => {
  const params = new URLSearchParams(window.location.search)

  for (const key of [...params.keys()]) {
    if (key === props.paramName || key === 'page' || isListKey(key)) {
      params.delete(key)
    }
  }

  const ids = (owners ?? []).map((owner) => owner.value)
  if (ids.length) {
    ids.forEach((id) => params.append(`${props.paramName}[]`, id))
  } else {
    // An explicit empty value stops the server re-applying its default.
    params.append(props.paramName, '')
  }

  props.navigate(`${window.location.pathname}?${params.toString()}${window.location.hash}`)
}
</script>

<style scoped>
:deep(.wds-selected-label) {
  max-width: 70%;
  vertical-align: top;
}
</style>
